import { CheckCircle2, ChevronRight, Circle, Pencil, X } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProductKey } from '../../lib/app-registry';
import type { RouterOutputs } from '../../lib/trpc';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';

export type OnboardingState = RouterOutputs['onboarding']['getState'];

type OnboardingStepAction =
  | { kind: 'rename'; label: string }
  | { kind: 'link'; label: string; to: string };

export type OnboardingStep = {
  id: string;
  title: string;
  description: string;
  isComplete: (state: OnboardingState) => boolean;
  isVisible?: (state: OnboardingState) => boolean;
  action?: OnboardingStepAction;
};

export interface OnboardingChecklistProps {
  steps: OnboardingStep[];
  workspaceLabel: string | undefined;
  className?: string;
  /** Resolves `state.productTier` for a step that depends on this product's billing tier (e.g. "start the trial"). Omit if no step needs it. */
  productKey?: ProductKey;
}

/**
 * A non-blocking, sidebar-sized setup checklist. The first completed workspace step gives a real
 * head start (goal-gradient effect), while all later completion is derived from live product data.
 */
export function OnboardingChecklist({
  steps,
  workspaceLabel,
  className,
  productKey,
}: OnboardingChecklistProps) {
  const utils = trpc.useUtils();
  const stateQuery = trpc.onboarding.getState.useQuery(productKey ? { productKey } : undefined);
  const dismissMutation = trpc.onboarding.dismiss.useMutation({
    onSuccess: () => utils.onboarding.getState.invalidate(),
  });
  const renameMutation = trpc.workspace.rename.useMutation({
    onSuccess: () => {
      utils.onboarding.getState.invalidate();
      utils.auth.me.invalidate();
      setRenameOpen(false);
    },
  });
  const [renameOpen, setRenameOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(workspaceLabel ?? '');

  useEffect(() => {
    if (renameOpen) setWorkspaceName(workspaceLabel ?? '');
  }, [renameOpen, workspaceLabel]);

  const visibleSteps = useMemo(() => {
    if (!stateQuery.data) return [];
    return steps.filter((step) => step.isVisible?.(stateQuery.data) ?? true);
  }, [stateQuery.data, steps]);
  const completedSteps = visibleSteps.filter(
    (step) => stateQuery.data && step.isComplete(stateQuery.data)
  );
  const isComplete = visibleSteps.length > 0 && completedSteps.length === visibleSteps.length;
  const progress =
    visibleSteps.length === 0 ? 0 : (completedSteps.length / visibleSteps.length) * 100;

  if (!stateQuery.data || stateQuery.data.dismissedAt || isComplete) return null;

  const handleRename = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const label = workspaceName.trim();
    if (label) renameMutation.mutate({ label });
  };

  return (
    <>
      <section
        aria-label="Workspace setup"
        className={cn('mx-2 my-2 rounded-lg border bg-muted/35 p-3 shadow-xs', className)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Set up your workspace</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {visibleSteps.length - completedSteps.length} step
              {visibleSteps.length - completedSteps.length === 1 ? '' : 's'} left
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-mr-1 -mt-1 size-7 text-muted-foreground"
            aria-label="Dismiss setup checklist"
            onClick={() => dismissMutation.mutate()}
            disabled={dismissMutation.isPending}
          >
            <X className="size-4" />
          </Button>
        </div>

        <Progress value={progress} className="mt-3 h-1.5" />

        <ol className="mt-3 space-y-1">
          {visibleSteps.map((step) => {
            const complete = step.isComplete(stateQuery.data);
            const action = step.action;
            return (
              <li key={step.id} className="rounded-md px-1 py-1.5">
                <div className="flex gap-2">
                  {complete ? (
                    <CheckCircle2
                      className="mt-0.5 size-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                  ) : (
                    <Circle
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'text-xs font-medium',
                        complete && 'text-muted-foreground line-through'
                      )}
                    >
                      {step.title}
                    </p>
                    {!complete && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
                    )}
                    {!complete && action?.kind === 'rename' && (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto px-0 py-1 text-xs"
                        onClick={() => setRenameOpen(true)}
                      >
                        <Pencil className="mr-1 size-3" />
                        {action.label}
                      </Button>
                    )}
                    {!complete && action?.kind === 'link' && (
                      <Button
                        asChild
                        type="button"
                        variant="link"
                        className="h-auto px-0 py-1 text-xs"
                      >
                        <Link to={action.to}>
                          {action.label}
                          <ChevronRight className="size-3" />
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Name your workspace</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace name</Label>
              <Input
                id="workspace-name"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                maxLength={100}
                autoFocus
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!workspaceName.trim() || renameMutation.isPending}>
                Save name
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
